import { useEffect, useState } from 'react';
import api from '../utils/api';
import { DollarSign, TrendingUp, Zap, Package, Users, Clock, Settings, RotateCcw } from 'lucide-react';
import PageHeader from '../components/PageHeader';

function StatCard({ icon: Icon, label, value, suffix = '원', color = 'text-white' }) {
  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
      <div className="flex items-center gap-2 mb-2">
        <Icon size={16} className="text-gray-500" />
        <span className="text-gray-400 text-sm">{label}</span>
      </div>
      <p className={`text-2xl font-bold ${color}`}>
        {typeof value === 'number' ? value.toLocaleString() : value}
        <span className="text-sm text-gray-500 ml-1">{suffix}</span>
      </p>
    </div>
  );
}

export default function CostingPage() {
  const [pnl, setPnl] = useState(null);
  const [days, setDays] = useState(30);
  const [showConfig, setShowConfig] = useState(false);
  const [cfgDraft, setCfgDraft] = useState({});
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get(`/costing/pnl?days=${days}`).then(r => { if (!cancelled) setPnl(r.data); });
    api.get('/costing/config').then(r => { if (!cancelled) setCfgDraft(r.data); });
    return () => { cancelled = true; };
  }, [days]);

  const load = () => {
    api.get(`/costing/pnl?days=${days}`).then(r => setPnl(r.data));
    api.get('/costing/config').then(r => setCfgDraft(r.data));
  };

  const saveConfig = async () => {
    await api.put('/costing/config', cfgDraft);
    setShowConfig(false);
    load();
  };

  const handleReset = async () => {
    if (!window.confirm('단가 설정을 기본값으로 초기화하시겠습니까?')) return;
    setResetting(true);
    try {
      await api.post('/costing/config/reset');
      load();
      window.alert('설정이 초기화되었습니다.');
    } catch (e) {
      window.alert('초기화 실패: ' + (e.response?.data?.message || e.message));
    } finally { setResetting(false); }
  };

  return (
    <div>
      <PageHeader icon={DollarSign} title="원가 · P&L 분석">
          <div className="flex gap-1">
            {[7, 30, 90].map(d => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={`px-3 py-1.5 rounded-lg text-xs ${days === d ? 'bg-brand text-white' : 'bg-gray-800 text-gray-400'}`}
              >{d}일</button>
            ))}
          </div>
          <button
            onClick={handleReset}
            disabled={resetting}
            className="flex items-center gap-1.5 px-3 py-2 bg-gray-800 text-gray-500 hover:text-red-400 rounded-lg text-sm disabled:opacity-40"
          >
            <RotateCcw size={14} /> 초기화
          </button>
          <button
            onClick={() => setShowConfig(true)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-800 text-gray-400 rounded-lg text-sm"
          >
            <Settings size={14} /> 단가 설정
          </button>
        </PageHeader>

      {pnl && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <StatCard icon={DollarSign} label="총 원가" value={pnl.totalCost} />
            <StatCard icon={TrendingUp} label="잡당 평균" value={pnl.avgCostPerJob} />
            <StatCard icon={Package} label="총 필라멘트" value={pnl.totalFilamentGram} suffix="g" />
            <StatCard icon={Clock} label="총 출력시간" value={pnl.totalPrintHours} suffix="시간" />
          </div>

          <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
            <h2 className="text-white font-semibold text-sm mb-4">원가 구성 ({pnl.jobCount}건)</h2>
            <div className="space-y-3">
              {[
                { key: 'materialCost', label: '재료비 (필라멘트)', icon: Package, color: 'bg-blue-500' },
                { key: 'electricityCost', label: '전기료', icon: Zap, color: 'bg-yellow-500' },
                { key: 'depreciationCost', label: '감가상각', icon: TrendingUp, color: 'bg-purple-500' },
                { key: 'laborCost', label: '인건비', icon: Users, color: 'bg-green-500' },
                { key: 'packagingCost', label: '포장비', icon: Package, color: 'bg-pink-500' },
                { key: 'shippingCost', label: '배송비', icon: Package, color: 'bg-orange-500' },
              ].map(item => {
                const value = pnl.breakdown[item.key] || 0;
                const pct = pnl.totalCost > 0 ? (value / pnl.totalCost) * 100 : 0;
                const Icon = item.icon;
                return (
                  <div key={item.key}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <Icon size={14} className="text-gray-500" />
                        <span className="text-white text-sm">{item.label}</span>
                      </div>
                      <div className="text-right">
                        <span className="text-white font-semibold">{value.toLocaleString()}원</span>
                        <span className="text-gray-500 text-xs ml-2">({pct.toFixed(1)}%)</span>
                      </div>
                    </div>
                    <div className="w-full bg-gray-800 rounded-full h-2">
                      <div className={`h-2 rounded-full ${item.color}`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {showConfig && cfgDraft && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 max-w-md w-full">
            <h2 className="text-white font-semibold mb-4">단가 설정</h2>
            <div className="space-y-3">
              {[
                { k: 'filamentPricePerGram', l: '필라멘트 단가 (원/g)' },
                { k: 'electricityPricePerKwh', l: '전기 단가 (원/kWh)' },
                { k: 'printerPowerKw', l: '프린터 정격 전력 (kW)' },
                { k: 'depreciationPerHour', l: '감가상각 (원/시간)' },
                { k: 'laborCostPerHour', l: '인건비 (원/시간)' },
                { k: 'standardLaborMinutesPerJob', l: '잡당 표준 인건시간 (분)' },
                { k: 'packagingCost', l: '포장비 (원/잡)' },
                { k: 'shippingCost', l: '배송비 (원/잡)' },
              ].map(f => (
                <div key={f.k}>
                  <label className="text-xs text-gray-400 block mb-1">{f.l}</label>
                  <input
                    type="number"
                    step="0.01"
                    value={cfgDraft[f.k] ?? ''}
                    onChange={e => setCfgDraft(c => ({ ...c, [f.k]: parseFloat(e.target.value) || 0 }))}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm"
                  />
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={() => setShowConfig(false)} className="flex-1 py-2 bg-gray-800 text-gray-400 rounded-lg text-sm">취소</button>
              <button onClick={saveConfig} className="flex-1 py-2 bg-brand text-white rounded-lg text-sm">저장</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
